/* ============================================================================
   BbLAB voices carousel runtime — 2026-10-08 (repaired 2026-10-08)

   One isolated singleton for `[data-voices-carousel]`. It owns:
   * a 72px/s continuous leftward rail with a seamless end-to-start wrap
     (runtime copies of the real figures, aria-hidden + inert, no ids and no
     Shopify editor block attributes);
   * autoplay that runs only while reduced-motion is off, the section is in the
     viewport, the document is visible, no focus/manual/editor/gesture/grace
     pause is active, and the first viewport images are decoded with no load
     failure;
   * pointer hover is intentionally NOT a pause condition: moving the mouse in
     or out never stops the rail, and it can never resume a manually paused one;
   * lazy progressive prefetch of the currently visible span plus 3–5 further
     cards through the real DOM <img> (eager + decode) so the browser never
     launches a second, differently sized URL;
   * a readiness gate that pauses before moving into an upcoming card whose
     image is not decoded, and stops on original or clone image failure;
   * keyboard-focus pause with resume on blur (unless manually paused or
     reduced-motion); pointer hover never pauses, a visible Japanese pause/resume
     control (>=44px, hidden and disabled without JS), native touch swipe plus a
     held-gesture pause
     (touchend/cancel/up then a short grace), ArrowLeft/Right + Home/End rail
     keys, and a `data-state` attribute for observability;
   * editor support: selecting a block scrolls its original into view and holds
     the rail paused; deselecting only clears that editor pause and never undoes
     an explicit manual pause;
   * section unload cancels rAF, observers, listeners, timers and clones, and
     clears the per-image flags so a reload on the same DOM can bind again.

   The asset installs once per document: a `document.__bblabVoicesModule`
   registration marker (a documented production singleton, not a test hook)
   stops a second script evaluation from adding duplicate lifecycle listeners
   while still re-initialising any markup rendered since.

   No Shopify, network or browser API is assumed to exist: every capability is
   feature-detected so the module also runs in the offline VM harness.
   ============================================================================ */
(function () {
  'use strict';

  var CAROUSEL = '[data-voices-carousel]';
  var RAIL = '[data-voices-rail]';
  var TOGGLE = '[data-voices-toggle]';
  var SPEED = 72;          // CSS px per second
  var GRACE_MS = 1200;     // pause after pointer/wheel/keyboard input
  var LOOKAHEAD = 5;       // extra prefetch window (task allows 3..5)
  var MIN_LOOKAHEAD = 3;

  function now() {
    if (typeof performance !== 'undefined' && performance && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  function list(scope, selector) {
    var root = scope || document;
    if (root.nodeType === 1 && root.matches && root.matches(selector)) return [root];
    return root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function closest(node, selector) {
    var cursor = node;
    while (cursor && cursor.nodeType === 1) {
      if (cursor.matches && cursor.matches(selector)) return cursor;
      cursor = cursor.parentElement;
    }
    return null;
  }

  // A cached broken image reports complete === true with naturalWidth 0, so
  // completeness alone is never enough to call an image usable.
  function imageReady(img) {
    if (!img || img.error) return false;
    return img.complete === true && typeof img.naturalWidth === 'number' && img.naturalWidth > 0;
  }

  function imageBroken(img) {
    if (!img) return false;
    if (img.error) return true;
    return img.complete === true && !(typeof img.naturalWidth === 'number' && img.naturalWidth > 0);
  }

  function createInstance(root) {
    if (root.__bblabVoices) return root.__bblabVoices;

    var rail = root.querySelector(RAIL);
    if (!rail) return null;

    var toggle = root.querySelector(TOGGLE);
    var originals = Array.prototype.slice.call(rail.children).filter(function (node) {
      return node.nodeType === 1 && !node.hasAttribute('data-voices-clone');
    });
    if (!originals.length) return null;

    var listeners = [];
    var observers = [];
    var clones = [];
    var pending = [];
    var prefetched = [];
    var flaggedImages = [];
    var raf = null;
    var lastTime = null;
    var graceTimer = null;
    var graceUntil = 0;
    var manual = false;
    var editorPaused = false;
    var gestureActive = false;
    var focused = false;
    var failed = false;
    var ready = false;
    var gathering = false;
    var gateBlocked = false;
    var visible = true;
    var documentVisible = !(typeof document.hidden === 'boolean' && document.hidden);
    var playing = false;
    var torn = false;

    var motion = (typeof window !== 'undefined' && window.matchMedia)
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    var reduced = !!(motion && motion.matches);

    function on(target, type, fn, options) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(type, fn, options);
      listeners.push([target, type, fn, options]);
    }

    // Hover is deliberately absent here and in shouldPlay(): pointer hover must
    // never pause a continuously looping rail, only focus/manual/editor/gesture
    // and the short input grace can.
    function state() {
      if (failed) return 'error';
      if (!ready) return 'idle';
      if (reduced) return 'reduced';
      if (manual) return 'manual';
      if (editorPaused) return 'editor';
      if (!visible || !documentVisible) return 'offscreen';
      if (focused || gestureActive || now() < graceUntil) return 'paused';
      return playing ? 'playing' : 'paused';
    }

    function paint() {
      var value = state();
      if (root.getAttribute('data-state') !== value) root.setAttribute('data-state', value);
      if (!toggle) return;
      toggle.hidden = false;
      toggle.disabled = reduced;
      toggle.setAttribute('aria-pressed', manual ? 'true' : 'false');
      if (reduced) {
        toggle.textContent = '自動再生オフ';
        toggle.setAttribute('aria-label', 'システムの設定により自動再生はオフです');
      } else {
        toggle.textContent = manual ? '再開' : '一時停止';
        toggle.setAttribute('aria-label', manual ? '自動スクロールを再開' : '自動スクロールを一時停止');
      }
    }

    function shouldPlay() {
      return !torn && !failed && ready && !gateBlocked && !reduced && !manual && !editorPaused &&
        !focused && !gestureActive && visible && documentVisible &&
        now() >= graceUntil;
    }

    function cloneOffset() {
      if (!clones.length || !originals.length) return 0;
      var first = originals[0];
      var clone = clones[0];
      if (typeof clone.offsetLeft !== 'number' || typeof first.offsetLeft !== 'number') return 0;
      return clone.offsetLeft - first.offsetLeft;
    }

    function step() {
      if (originals.length < 2) return 0;
      var first = originals[0];
      var second = originals[1];
      var size = (second.offsetLeft - first.offsetLeft) ||
        (typeof first.offsetWidth === 'number' && first.offsetWidth) || 0;
      return size;
    }

    function visibleSpan() {
      var size = step();
      if (size > 0 && typeof rail.clientWidth === 'number' && rail.clientWidth > 0) {
        return Math.max(1, Math.ceil(rail.clientWidth / size));
      }
      return 1;
    }

    // Scroll position maps onto the cycle modulo the original set, so the
    // hidden clone region keeps feeding the same lookahead window.
    function currentIndex() {
      if (!rail || typeof rail.scrollLeft !== 'number') return 0;
      var size = step();
      if (size > 0) {
        var raw = Math.floor(rail.scrollLeft / size);
        return ((raw % originals.length) + originals.length) % originals.length;
      }
      var best = 0;
      for (var i = 0; i < originals.length; i += 1) {
        if (originals[i].offsetLeft - rail.scrollLeft <= 1) best = i;
      }
      return best;
    }

    function pendingAdd(img) {
      if (pending.indexOf(img) === -1) pending.push(img);
    }

    function pendingRemove(img) {
      var position = pending.indexOf(img);
      if (position !== -1) pending.splice(position, 1);
    }

    function fail() {
      if (failed) return;
      failed = true;
      pending.length = 0;
      update();
    }

    function settle() {
      if (failed) return;
      if (!gathering && !ready && pending.length === 0) {
        ready = true;
        prefetchFrom(0);
      }
      update();
    }

    // Readiness waits for a successful decode where supported; a rejected
    // decode is a failure, never silently "ready". Without decode support it
    // falls back to the real load event (see watchImage).
    function decodeOrSettle(img) {
      if (failed) { pendingRemove(img); return; }
      if (typeof img.decode === 'function') {
        var attempt;
        try {
          attempt = img.decode();
        } catch (error) {
          pendingRemove(img);
          fail();
          return;
        }
        if (attempt && typeof attempt.then === 'function') {
          attempt.then(
            function () {
              // A decode that settles after teardown must not promote images or
              // touch state that a reloaded instance now owns.
              if (torn) return;
              pendingRemove(img);
              settle();
            },
            function () {
              if (torn) return;
              pendingRemove(img);
              fail();
            }
          );
          return;
        }
      }
      pendingRemove(img);
      settle();
    }

    function flagImage(img) {
      if (img && flaggedImages.indexOf(img) === -1) flaggedImages.push(img);
    }

    function bindError(img) {
      if (!img || img.__bblabVoicesErrorBound) return;
      img.__bblabVoicesErrorBound = true;
      flagImage(img);
      on(img, 'error', onImageError);
    }

    function watchImage(img) {
      if (!img || img.__bblabVoicesWatched || failed) return;
      img.__bblabVoicesWatched = true;
      flagImage(img);
      bindError(img);
      if (imageBroken(img)) { pendingRemove(img); fail(); return; }
      if (imageReady(img)) { pendingAdd(img); decodeOrSettle(img); return; }
      pendingAdd(img);
      on(img, 'load', function () { decodeOrSettle(img); });
    }

    function watchFirstViewport() {
      var width = typeof rail.clientWidth === 'number' ? rail.clientWidth : 0;
      var needed = originals.filter(function (figure) {
        return width <= 0 || figure.offsetLeft <= width + 1;
      });
      if (!needed.length) needed = [originals[0]];
      gathering = true;
      needed.forEach(function (figure) { watchImage(figure.querySelector('img')); });
      gathering = false;
      if (!failed && !ready && pending.length === 0) {
        ready = true;
        prefetchFrom(0);
      }
    }

    // Re-check the images currently framed by the rail (used on resize).
    function watchVisible() {
      var left = typeof rail.scrollLeft === 'number' ? rail.scrollLeft : 0;
      var width = typeof rail.clientWidth === 'number' ? rail.clientWidth : 0;
      originals.forEach(function (figure) {
        var start = figure.offsetLeft;
        var end = start + (typeof figure.offsetWidth === 'number' ? figure.offsetWidth : 0);
        if (end >= left && start <= left + width) watchImage(figure.querySelector('img'));
      });
    }

    function stripCloneAttributes(node) {
      if (!node || !node.getAttributeNames) return;
      node.getAttributeNames().forEach(function (name) {
        if (name === 'id' || name.indexOf('data-shopify') === 0) node.removeAttribute(name);
      });
    }

    function buildClones() {
      if (clones.length) return;
      originals.forEach(function (figure) {
        var clone = figure.cloneNode(true);
        if (!clone) return;
        clone.setAttribute('data-voices-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        var nodes = [clone].concat(
          clone.querySelectorAll ? Array.prototype.slice.call(clone.querySelectorAll('*')) : []
        );
        nodes.forEach(stripCloneAttributes);
        rail.appendChild(clone);
        clones.push(clone);
        var cloneImg = clone.querySelector ? clone.querySelector('img') : null;
        if (cloneImg) bindError(cloneImg);
      });
    }

    // Prefetch the real DOM <img>: promote it to eager and decode it. It never
    // creates a second Image() at a different width, which could make the
    // browser fetch both the 480 and 800 candidate.
    function prefetchFrom(start) {
      var count = Math.max(MIN_LOOKAHEAD, Math.min(LOOKAHEAD, originals.length - 1));
      var total = visibleSpan() + count;
      for (var offset = 0; offset < total; offset += 1) {
        var index = ((start + offset) % originals.length + originals.length) % originals.length;
        var figure = originals[index];
        if (!figure || prefetched.indexOf(figure) !== -1) continue;
        prefetched.push(figure);
        var img = figure.querySelector('img');
        if (!img) continue;
        bindError(img);
        if (img.loading !== 'eager') img.loading = 'eager';
        if (typeof img.decode === 'function') {
          try {
            var attempt = img.decode();
            if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
          } catch (error) { /* decode() is best effort */ }
        }
      }
    }

    // The card about to enter from the right must already be usable; otherwise
    // the rail holds still instead of advancing into an empty card.
    function leadReady() {
      var leadIndex = (currentIndex() + visibleSpan()) % originals.length;
      var figure = originals[leadIndex];
      var img = figure && figure.querySelector('img');
      if (!img) return true;
      bindError(img);
      if (imageBroken(img)) { fail(); return false; }
      if (imageReady(img)) return true;
      watchImage(img);
      if (img.loading !== 'eager') img.loading = 'eager';
      return false;
    }

    function onImageError(event) {
      var img = event && event.currentTarget;
      if (img) pendingRemove(img);
      fail();
    }

    function frame(time) {
      raf = null;
      if (!playing || torn) return;
      if (lastTime === null) lastTime = time;
      var delta = time - lastTime;
      lastTime = time;
      var advance = delta > 0 ? (SPEED * delta) / 1000 : 0;
      if (advance > 0 && typeof rail.scrollLeft === 'number') {
        if (!leadReady()) {
          gateBlocked = true;
          playing = false;
          update();
          return;
        }
        rail.scrollLeft += advance;
        var wrap = cloneOffset();
        if (wrap > 0 && rail.scrollLeft >= wrap) rail.scrollLeft -= wrap;
      }
      prefetchFrom(currentIndex());
      if (shouldPlay()) {
        raf = requestAnimationFrame(frame);
      } else {
        playing = false;
        paint();
      }
    }

    function update() {
      if (torn) return;
      if (!failed && gateBlocked && leadReady()) gateBlocked = false;
      if (shouldPlay()) {
        if (!playing) {
          playing = true;
          lastTime = null;
          raf = requestAnimationFrame(frame);
        }
      } else if (playing) {
        playing = false;
        if (raf !== null) {
          cancelAnimationFrame(raf);
          raf = null;
        }
      }
      paint();
    }

    function grace() {
      graceUntil = now() + GRACE_MS;
      if (graceTimer !== null) clearTimeout(graceTimer);
      graceTimer = setTimeout(function () {
        graceTimer = null;
        update();
      }, GRACE_MS + 20);
      update();
    }

    // A held finger/pointer keeps the rail paused for the whole gesture; the
    // grace clock only starts on touchend/cancel/up. Normal vertical page
    // scroll is untouched because touch listeners stay passive.
    function beginGesture() {
      gestureActive = true;
      graceUntil = 0;
      if (graceTimer !== null) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
      update();
    }

    function endGesture() {
      if (!gestureActive) return;
      gestureActive = false;
      grace();
    }

    // End always lands on the last ORIGINAL, never the hidden duplicate cycle.
    function originalEndOffset() {
      if (typeof rail.scrollLeft !== 'number') return 0;
      var last = originals[originals.length - 1];
      if (last && typeof last.offsetLeft === 'number') {
        var width = typeof last.offsetWidth === 'number' ? last.offsetWidth : 0;
        return Math.max(0, last.offsetLeft + width - rail.clientWidth);
      }
      var wrap = cloneOffset();
      if (wrap > 0) return Math.max(0, wrap - rail.clientWidth);
      var scrollWidth = typeof rail.scrollWidth === 'number' ? rail.scrollWidth : 0;
      return Math.max(0, scrollWidth - rail.clientWidth);
    }

    function onResize() {
      if (torn) return;
      var index = currentIndex();
      var size = step();
      if (size > 0 && typeof rail.scrollLeft === 'number') {
        var target = index * size;
        var wrap = cloneOffset();
        if (wrap > 0) target = ((target % wrap) + wrap) % wrap;
        rail.scrollLeft = Math.max(0, target);
      }
      watchVisible();
      prefetchFrom(currentIndex());
      update();
    }

    function scrollIntoView(figure) {
      if (!figure) return;
      if (typeof figure.scrollIntoView === 'function') {
        figure.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
        return;
      }
      if (typeof rail.scrollLeft === 'number') {
        rail.scrollLeft = Math.max(0, figure.offsetLeft - rail.clientWidth / 2);
      }
    }

    function pauseForEditor(target) {
      editorPaused = true;
      var figure = target && target.nodeType === 1 ? target : null;
      if (figure && figure.hasAttribute && figure.hasAttribute('data-voices-clone')) {
        var cloneIndex = clones.indexOf(figure);
        figure = cloneIndex >= 0 ? originals[cloneIndex] : null;
      }
      if (!figure) figure = closest(target, '[data-voices-item]');
      update();
      scrollIntoView(figure);
    }

    function resumeEditor() {
      if (!editorPaused) return;
      editorPaused = false;
      update();
    }

    function wire() {
      buildClones();
      watchFirstViewport();
      originals.forEach(function (figure) {
        var img = figure.querySelector('img');
        if (img) bindError(img);
      });

      // Keyboard focus still pauses so a keyboard/screen-reader user is not
      // chased by movement. Pointer hover has no listener at all: the rail keeps
      // scrolling continuously on pointerenter and pointerleave.
      on(root, 'focusin', function () { focused = true; update(); });
      on(root, 'focusout', function () { focused = false; update(); });

      if (toggle) {
        on(toggle, 'click', function () {
          manual = !manual;
          update();
        });
      }

      if (rail) {
        on(rail, 'pointerdown', beginGesture);
        on(rail, 'pointerup', endGesture);
        on(rail, 'pointercancel', endGesture);
        on(rail, 'touchstart', beginGesture, { passive: true });
        on(rail, 'touchend', endGesture, { passive: true });
        on(rail, 'touchcancel', endGesture, { passive: true });
        on(rail, 'wheel', grace, { passive: true });
        on(rail, 'keydown', function (event) {
          var key = event.key;
          var delta = step() || 230;
          if (key === 'ArrowRight') rail.scrollLeft += delta;
          else if (key === 'ArrowLeft') rail.scrollLeft = Math.max(0, rail.scrollLeft - delta);
          else if (key === 'Home') rail.scrollLeft = 0;
          else if (key === 'End') rail.scrollLeft = originalEndOffset();
          else return;
          if (event.preventDefault) event.preventDefault();
          grace();
        });
      }

      if (motion) {
        var onMotion = function () {
          reduced = !!motion.matches;
          update();
        };
        if (motion.addEventListener) {
          motion.addEventListener('change', onMotion);
          listeners.push([motion, 'change', onMotion]);
        } else if (motion.addListener) {
          motion.addListener(onMotion);
          listeners.push([motion, 'change', onMotion]);
        }
      }

      if (typeof IntersectionObserver === 'function') {
        var observer = new IntersectionObserver(function (entries) {
          var entry = entries && entries[entries.length - 1];
          if (entry) visible = !!entry.isIntersecting;
          update();
        }, { threshold: 0.01 });
        observer.observe(root);
        observers.push(observer);
      }

      on(document, 'visibilitychange', function () {
        documentVisible = !(typeof document.hidden === 'boolean' && document.hidden);
        update();
      });

      if (typeof window !== 'undefined') on(window, 'resize', onResize);

      update();
    }

    function teardown() {
      if (torn) return;
      torn = true;
      playing = false;
      if (raf !== null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      if (graceTimer !== null) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
      observers.forEach(function (observer) {
        if (observer && observer.disconnect) observer.disconnect();
      });
      observers = [];
      listeners.forEach(function (entry) {
        if (entry[0] && entry[0].removeEventListener) entry[0].removeEventListener(entry[1], entry[2], entry[3]);
      });
      listeners.length = 0;
      clones.forEach(function (clone) {
        if (clone.parentElement && clone.parentElement.removeChild) clone.parentElement.removeChild(clone);
      });
      clones.length = 0;
      pending.length = 0;
      prefetched.length = 0;
      // Drop the per-image flags together with the listeners so a reload on the
      // same DOM can bind its error/load handlers again.
      flaggedImages.forEach(function (img) {
        try {
          delete img.__bblabVoicesErrorBound;
          delete img.__bblabVoicesWatched;
        } catch (error) {
          img.__bblabVoicesErrorBound = false;
          img.__bblabVoicesWatched = false;
        }
      });
      flaggedImages.length = 0;
      root.removeAttribute('data-state');
      root.__bblabVoices = null;
    }

    var instance = {
      root: root,
      rail: rail,
      originals: originals,
      clones: clones,
      update: update,
      teardown: teardown,
      state: state,
      pauseForEditor: pauseForEditor,
      resumeEditor: resumeEditor
    };
    root.__bblabVoices = instance;
    wire();
    return instance;
  }

  function init(scope) {
    list(scope || document, CAROUSEL).forEach(createInstance);
  }

  function teardown(scope) {
    list(scope || document, CAROUSEL).forEach(function (root) {
      if (root.__bblabVoices) root.__bblabVoices.teardown();
    });
  }

  // Genuine once-per-document registration. A repeated evaluation (for example
  // the theme editor re-inserting the section script) re-inits current markup
  // instead of registering a second set of lifecycle listeners.
  function install(doc) {
    if (doc.__bblabVoicesModule) {
      doc.__bblabVoicesModule.init(doc);
      return doc.__bblabVoicesModule;
    }
    var registration = { init: init, teardown: teardown };
    doc.__bblabVoicesModule = registration;

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', function () { init(doc); });
    } else {
      init(doc);
    }

    doc.addEventListener('shopify:section:load', function (event) { init(event.target); });
    doc.addEventListener('shopify:section:unload', function (event) { teardown(event.target); });
    doc.addEventListener('shopify:block:select', function (event) {
      var root = closest(event.target, CAROUSEL);
      var instance = root && root.__bblabVoices;
      if (!instance) return;
      instance.pauseForEditor(event.target);
    });
    doc.addEventListener('shopify:block:deselect', function (event) {
      var root = closest(event.target, CAROUSEL);
      var instance = root && root.__bblabVoices;
      if (!instance) return;
      instance.resumeEditor();
    });
    return registration;
  }

  install(document);
})();
